import * as services from '@services/document'
import * as types from '@types'
import { INVALID_CPF_ERROR } from '@content/consts/Documents'
import defaults from './defaults'

export default {
  state: {
    documents: { ...defaults },
    customDocuments: {}
  },

  getters: {
    [types.DOCUMENT_GET_DOCUMENTS]: state => ({
      ...state.documents,
      ...state.customDocuments,
    })
  },

  mutations: {
    [types.DOCUMENT_RESET]: (state) => {
      state.documents = { ...defaults }
      state.customDocuments = {}
    },

    [types.DOCUMENT_SET_STATUS]: (state, { type, status }) => {
      const document = state.documents[type] || state.customDocuments[type]

      if (!document) return

      document.status = status
    },

    [types.DOCUMENT_SET_FILES]: (state, { type, files }) => {
      const document = state.documents[type] || state.customDocuments[type]

      if (!document) return

      document.files = files?.map(file => ({ ...file, progress: 0, uploading: false })) || []
    },

    [types.DOCUMENT_CUSTOM_FIELDS]: (state, customFields) => {
      Object.entries(customFields)
        .forEach(([ type, fields ]) => {
          const isCustom = !!state.customDocuments[type]
          const document = state.documents[type] || state.customDocuments[type]

          if (!document) return

          const reduceValues = (acc, [ id, field ]) => ({ [id]: field.value, ...acc })

          const customFieldValues = Object.entries(fields).reduce(reduceValues, {})

          document.data = { ...document.data, ...customFieldValues }
          document.customFields = { ...fields }

          if (isCustom) {
            document.requiredFiles = Object.values(fields)
              .findIndex(field => field.type === 'file' && field.required) >= 0
          }
        })
    },

    [types.DOCUMENT_CUSTOM_DOCUMENTS]: (state, customDocuments) => {
      state.customDocuments = customDocuments
        .reduce((acc, { id, ...document }) => ({
          ...acc,
          [id]: {
            id,
            title: document.label,
            status: state.customDocuments[id]?.status || 'default',
            files: document.files,
            filesPlaceholder: [],
            filesLimit: 4,
            filesSlots: false,
            customFields: {},
            data: {},
            requiredFiles: false,
            requiredFields: [],
            customFieldValueId: document.customFieldValueId,
          }
        }), {})
    },

    [types.DOCUMENT_SET_RG]: (state, value) => {
      const { id, files, ...data } = value || {}
      const document = state.documents.rg || {}

      state.documents.rg = { ...document, id, files, data }
    },

    [types.DOCUMENT_SET_CNH]: (state, value) => {
      const { id, files, ...data } = value || {}
      const document = state.documents.driver_license || {}

      state.documents.driver_license = { ...document, id, files, data }
    },

    [types.DOCUMENT_SET_CPF]: (state, value) => {
      const { id, files, ...data } = value || {}
      const document = state.documents.cpf || {}

      state.documents.cpf = { ...document, id, files, data }
    },

    [types.DOCUMENT_SET_RESERVIST]: (state, value) => {
      const { id, files, ...data } = value || {}
      const document = state.documents.reservist || {}

      state.documents.reservist = { ...document, id, files, data }
    },

    [types.DOCUMENT_SET_ELECTORAL]: (state, value) => {
      const { id, files, ...data } = value || {}
      const document = state.documents.electoral_card || {}

      state.documents.electoral_card = { ...document, id, files, data }
    },

    [types.DOCUMENT_SET_CTPS]: (state, data) => {
      const { id, files, ...value } = data || {}
      const document = state.documents.ctps || {}

      state.documents.ctps = { ...document, id, files, data: value }
    },

    [types.DOCUMENT_SET_CUSTOM]: (state, data) => {
      const document = state.customDocuments[data.id]

      if (!document) return

      document.data = data
    },

    [types.DOCUMENT_REQUIRED_FIELDS]: (state, { requiredFields }) => {
      const modelMap = {
        'Cpf': 'cpf',
        'Ctps': 'ctps',
        'Reservist': 'reservist',
        'GeneralRegisters': 'rg',
        'DriverLicense': 'driver_license',
        'ElectoralCard': 'electoral_card',
      }

      Object.entries(requiredFields)
        .forEach(([ model, fields ]) => {
          const type = modelMap[model]
          const document = state.documents[type] || state.customDocuments[type]

          if (!document) return

          document.requiredFiles = fields.includes('attachment')
          document.requiredFields = [
            ...document.requiredFields,
            ...fields.filter(key => key !== 'attachment')
          ]
        })
    }
  },

  actions: {
    [types.DOCUMENT_GET_DOCUMENTS]: async ({ dispatch }) => {
      return Promise.allSettled([
        dispatch(types.DOCUMENT_GET_RG),
        dispatch(types.DOCUMENT_GET_CPF),
        dispatch(types.DOCUMENT_GET_DRIVER_LICENSE),
        dispatch(types.DOCUMENT_GET_RESERVIST),
        dispatch(types.DOCUMENT_GET_ELECTORAL),
        dispatch(types.DOCUMENT_GET_CTPS),
        dispatch(types.DOCUMENT_REQUIRED_FIELDS),
      ])
    },

    [types.DOCUMENT_CUSTOM_FIELDS]: async ({ commit, getters }) => {
      const [ err, customFields ] = await services.getCustomFields(getters)

      if (!err) commit(types.DOCUMENT_CUSTOM_FIELDS, customFields)

      return [ err, customFields ]
    },

    [types.DOCUMENT_CUSTOM_DOCUMENTS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, customDocuments ] = await services.getCustomDocuments(params)

      if (!err) commit(types.DOCUMENT_CUSTOM_DOCUMENTS, customDocuments)

      return [ err, customDocuments ]
    },

    [types.DOCUMENT_REQUIRED_FIELDS]: async ({ commit, getters }) => {
      const { requiredFields = [] } = getters || {}

      commit(types.DOCUMENT_REQUIRED_FIELDS, { requiredFields })
    },

    [types.DOCUMENT_GET_RG]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getGeneralRegisters({ employeeId, companyId })

      if (!err) commit(types.DOCUMENT_SET_RG, data)

      return [ err, data ]
    },

    [types.DOCUMENT_SET_RG]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.rg?.customFields || {}
      const params = { employeeId, companyId, schema }

      const [ err, document ] = await services.updateGeneralRegisters(data, params)

      if (!err) commit(types.DOCUMENT_SET_RG, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_CPF]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getCpf(params)

      if (!err) commit(types.DOCUMENT_SET_CPF, data)

      return [ err, data ]
    },

    [types.DOCUMENT_GET_RESERVIST]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const params = { employeeId, companyId }

      const [ err, data ] = await services.getReservist(params)

      if (!err) commit(types.DOCUMENT_SET_RESERVIST, data)

      return [ err, data ]
    },

    [types.DOCUMENT_SET_RESERVIST]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.reservist?.customFields || {}
      const params = { employeeId, companyId, schema }

      const [ err, document ] = await services.updateReservist(data, params)

      if (!err) commit(types.DOCUMENT_SET_RESERVIST, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_FORM_FEEDBACK]: async ({ dispatch }, err) => {
      dispatch('FEEDBACKS_ADD', {
        type: err ? 'error' : 'success',
        message: err ? 'Erro' : 'Documento',
        highlighted: err
          ? 'ao salvar o documento. Tente novamente mais tarde.'
          : 'salvo com sucesso.'
      })
    },

    [types.DOCUMENT_SET_CNH]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.driver_license?.customFields || {}

      const [ err, document ] = await services.updateDriverLicense(
        data,
        { employeeId, companyId, schema }
      )

      if (!err) commit(types.DOCUMENT_SET_CNH, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_DRIVER_LICENSE]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getDriverLicense({ employeeId, companyId })

      if (!err) commit(types.DOCUMENT_SET_CNH, data)

      return [ err, data ]
    },

    [types.DOCUMENT_SET_CPF]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.cpf?.customFields || {}

      const [ err, document ] = await services.updateCpf(data, { employeeId, companyId, schema })

      if (err) {
        const uniqueCpfErr = Array.isArray(err) && err
          .some(message => message.includes(INVALID_CPF_ERROR))

        dispatch('FEEDBACKS_ADD', {
          type: 'error',
          message: uniqueCpfErr ? 'CPF' : 'Erro',
          highlighted: uniqueCpfErr
            ? 'deve ser único dentro da empresa. Por favor, converse com o RH'
            : 'ao salvar o documento. Tente novamente mais tarde.'
        })

        return [ err, null ]
      }

      if (!err) commit(types.DOCUMENT_SET_CPF, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_GET_ELECTORAL]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getElectoralCard({ employeeId, companyId })

      if (!err) commit(types.DOCUMENT_SET_ELECTORAL, data)

      return [ err, data ]
    },

    [types.DOCUMENT_SET_ELECTORAL]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents?.electoral_card?.customFields || {}

      const [ err, document ] = await services.updateElectoralCard(
        data,
        { employeeId, companyId, schema }
      )

      if (!err) commit(types.DOCUMENT_SET_ELECTORAL, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_SET_CTPS]: async ({ state, commit, getters, dispatch }, data) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.documents.ctps.customFields

      const [ err, document ] = await services.updateCtps(data, { employeeId, companyId, schema })

      if (!err) commit(types.DOCUMENT_SET_CTPS, document)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      return [ err, document ]
    },

    [types.DOCUMENT_SET_CUSTOM]: async ({ state, getters, dispatch }, data) => {
      if (!data) return []

      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid
      const schema = state.customDocuments[data.id].customFields
      const params = { employeeId, companyId, schema }

      const [ err ] = await services.updateCustomDocument(data, params)

      dispatch(types.DOCUMENT_FORM_FEEDBACK, err)

      if (!err) {
        await dispatch(types.DOCUMENT_CUSTOM_DOCUMENTS)

        return [ null, data ]
      }

      return [ err, null ]
    },

    [types.DOCUMENT_VALIDATE_DOCUMENTS]: async ({ state, commit }, params) => {
      const { validated = [], errors = [] } = params

      let isValid = true

      Object.entries({ ...state.documents, ...state.customDocuments })
        .forEach(([ type ]) => {
          const valid = validated.includes(type)
          const error = errors.includes(type)

          commit(types.DOCUMENT_SET_STATUS, {
            type,
            status: valid ? 'success' : error ? 'error' : 'default'
          })

          if (isValid)
            isValid = valid && !error
        })

      return isValid
    },

    [types.DOCUMENT_GET_CTPS]: async ({ commit, getters }) => {
      const employeeId = getters.getEmployeeUuid
      const companyId = getters.getCompanyUuid

      const [ err, data ] = await services.getCtps({ employeeId, companyId })

      if (!err) commit(types.DOCUMENT_SET_CTPS, data)

      return [ err, data ]
    },

    [types.DOCUMENT_UPLOAD_FILES]: async ({ getters }, { id, files, type }) => {
      const employeeId = getters.getEmployeeUuid
      const params = { id, files, type, employeeId }

      const [ err ] = await services.uploadDocumentFiles(params)

      return [ err, null ]
    },
  }
}
